export enum EReportDataType {
  temp = 'temporary',
  mask = 'mask',
}

export interface IGps {
  id: number;
  deviceUniqueId: string;
  timestamp: number;
  dateTime: Date;
  heading: number;
  altitude: number;
  latitude: number;
  longitude: number;
  polyline: string;
  speed: number;
  distance: number;
  geoidal: number;
  dataType?: EReportDataType | null;
  createdAt: Date;
  updatedAt: Date;
  stats: any;
}

export interface ILogGPS {
  [key: string]: IGps;
}

export enum EFlowmeterType {
  port = 'port',
  starboard = 'starboard',
  center = 'center',
}

export enum EFlowmeterFlowType {
  in = 'in',
  out = 'out',
}

export interface IFlowmeter {
  id: number;
  deviceUniqueId: string;
  timestamp: number;
  dateTime: string;
  density: number;
  temperature: number;
  volumeFlowrate: number;
  volumeInventory: number;
  volumeTotal: number;
  massFlowrate: number;
  massInventory: number;
  massTotal: number;
  dataType?: EReportDataType | null;
  createdAt: Date;
  updatedAt: Date;
  stats: any;
}

export interface IFlowmeterCalculation {
  volumeFlowrate: number;
  massFlowrate: number;
  density: number;
  fuelCons: number;
  volumeFuelCons: number;
  temperature: number;
}

export interface ILogFlowmeter {
  [key: string]: {
    deviceCalculation: {
      [key: string]: IFlowmeterCalculation;
    };
    totalFuelCons: number;
    totalFuelConsVolume: number;
  } & {
    [key: string]: IFlowmeter;
  };
}

export interface IAE {
  id: number;
  deviceUniqueId: string;
  timestamp: number;
  datetime: Date;
  fuelConsumption: number;
  rpm: number;
  voltage: number;
  ampere: number;
  runningTime: number;
  type: number;
  dataType?: EReportDataType | null;
  createdAt: Date;
  updatedAt: Date;
  stats?: any;
  density?: number;
  temperature?: number;
  volumeFlowrate?: number;
  volumeInventory?: number;
  volumeTotal?: number;
  massFlowrate?: number;
  massInventory?: number;
  massTotal?: number;
}

export interface ILogAE {
  [key: string]: {
    totalFuelConsumption: number;
    totalRunningTime: number;
    totalFuelCons?: number;
    totalFuelConsVolume?: number;
  } & {
    [key: string]: IAE;
  };
}

export enum ERPMType {
  port = 'port',
  starboard = 'starboard',
  center = 'center',
}

export interface IRpm {
  id: number;
  deviceUniqueId: string;
  timestamp: number;
  datetime: Date;
  rpm: number;
  runningTime: number;
  type: ERPMType;
  dataType?: EReportDataType | null;
  createdAt: Date;
  updatedAt: Date;
  stats?: any;
}

export interface IClutch {
  id: number;
  deviceUniqueId: string;
  timestamp: number;
  datetime: Date;
  clutch: number;
  runningTime: number;
  type: ERPMType;
  dataType?: EReportDataType | null;
  createdAt: Date;
  updatedAt: Date;
  stats?: any;
}
export interface IOperationModeBreakdown {
  name: string;
  runningTime: number;
  consRate: number;
  minutes: number;
}

export interface IOperationMode {
  id: number;
  deviceUniqueId: string;
  timestamp: number;
  datetime: Date;
  rpm: number;
  runningTime: number;
  rpmMax: number | null;
  clutch?: number | null;
  clutchMax?: number | null;
  mode: string;
  consRate: number | null;
  speedMissing?: boolean;
  /**
   * An hour or a day bucket: the modes of its minutes, `mode` being the one of
   * most minutes and `runningTime`/`consRate` (liter) their sums.
   */
  modes?: IOperationModeBreakdown[];
  type: ERPMType;
  dataType?: EReportDataType | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ILogRpm {
  [key: string]: {
    totalRpm: number;
    totalRunningTime: number;
  } & {
    [key: string]: IRpm;
  };
}

export interface ILogClutch {
  [key: string]: {
    totalClutch: number;
    totalRunningTime: number;
  } & {
    [key: string]: IClutch;
  };
}

export interface ILogOperationMode {
  [key: string]: {
    totalConsRate: number;
    totalMaxRpm: number;
    totalRunningTime: number;
  } & {
    [key: string]: IOperationMode;
  };
}

export interface IGateway {
  id: number;
  deviceUniqueId: string;
  timestamp: number;
  datetime: Date;
  cpuUsage: number;
  memoryFree: number;
  spaceFree: number;
  signalStrength: number;
  dataType?: EReportDataType | null;
  createdAt: Date;
  updatedAt: Date;
}

export enum EPanelDoorName {
  bridge = 'doorBridge',
  engine = 'doorEngine',
}

export enum EPanelDoorState {
  closed = 0,
  open = 1,
}

export interface IPanelDoor {
  id: number;
  deviceUniqueId: string;
  timestamp: number;
  dateTime: Date | string;
  doorName: EPanelDoorName;
  state: EPanelDoorState;
  createdAt: Date;
  updatedAt: Date;
}

export interface ILogPanelDoor {
  [key: string]: IPanelDoor;
}

export interface ILogGateway {
  [key: string]: IGateway;
}

export enum EReportDataInterval {
  minute = 'minute',
  hour = 'hour',
  day = 'day',
  week = 'week',
  month = 'month',
}

export interface ILogReport {
  gps?: ILogGPS;
  flowmeter?: ILogFlowmeter;
  ae?: ILogAE;
  rpm?: ILogRpm;
  clutch?: ILogClutch;
  gateway?: ILogGateway;
  operation_mode?: ILogOperationMode;
  panel_door?: ILogPanelDoor;
}

/**
 * GET /report-log/summary: the summary calculated by the database over the
 * minutes of the range, with the keys of generateReportSummary. gps is in
 * meter and the speed unit of the log, formatGpsSummary converts it.
 */
export type IReportSummary = Record<string, any> & {
  totalEngineFuelCons: number;
  totalAEFuelCons: number;
  totalAERunningTime: number;
  gps: {
    count: number;
    avgSpeed: number;
    totalCruise: number;
    totalDistance: number;
  };
};

export interface ILogReportFilter {
  deviceType: string;
  dataSource?: string;
  assetId: string;
  interval: any;
  startAt: number;
  endAt: number;
  allowWeeklyMinute?: boolean;
  assetIds?: string[];
  // rangeType: ELogFilterRangeType;
  timezone?: number;
  deviceId?: string;
  deviceUniqueId?: string;
}

export interface IReportLogGPS {}

export enum ELogFilterRangeType {
  'last_30_day' = 'last_30_day',
  'last_month' = 'last_month',
  'last_7_day' = 'last_7_day',
  today = 'today',
  'last_day' = 'last_day',
  custom = 'custom',
}

export interface IDataAE {
  ae1_RunningTime: string;
  ae1_FuelConsumption: string;
  ae2_RunningTime: string;
  ae2_FuelConsumption: string;
  ae3_RunningTime: string;
  ae3_FuelConsumption: string;
  dateTime: '';
}

export const initialIDataAE: IDataAE = {
  ae1_RunningTime: '',
  ae1_FuelConsumption: '',
  ae2_RunningTime: '',
  ae2_FuelConsumption: '',
  ae3_RunningTime: '',
  ae3_FuelConsumption: '',
  dateTime: '',
};
export interface CapturePdfParams {
  assetId: string;
  assetName: string;
  startAt: number;
  endAt: number;
  deviceType?: string;
  interval?: string;
  timezone?: number;
  scheduleReport?: number;
  dataDownloaded?: string[];
  dataType?: number;
  distanceUnit?: string;
  speedUnit?: string;
  cruiseUnit?: string;
  /**
   * Legacy flag forwarded into /report-pdf URL so DailyReport can request
   * minute-interval data up to ~1 month when the company is opted in.
   */
  allowWeeklyMinute?: boolean | string;
}

export type vesselTrackingType = {
  key: string;
  header: string;
  position: string;
  time: string;
  speed: string;
};
